// TrendPeak_Analysis.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getChhattisgarhDailyPeak,
  getChhattisgarhDailyForecastedPeak,
  getChhattisgarhMonthlyPeakDiscom,
  getChhattisgarhMonthlyPeakForecast,
} from "../Utility/RestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { TrendData } from "../../../models/metrics";
import { formatDateForAPI } from "../const";
// ─── Constants ────────────────────────────────────────────────────────────────

const X_AXIS = [
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const DISCOM_OPTIONS = ["CSPDCL"];
const UNITS = ["MW", "MWh", "LU"];

// All possible peak metric keys, tagged by their data type
const ALL_PEAK_METRICS = [
  // Actual Load metrics
  {
    dataKey: "_peak_mw",
    displayName: "Day Peak",
    type: "demand",
    blockKey: "peak_slot",
    blockLabel: "Day Peak Block",
  },
  {
    dataKey: "_morning_peak_mw",
    displayName: "Morning Peak",
    type: "demand",
    blockKey: "morning_peak_slot",
    blockLabel: "Morning Peak Block",
  },
  {
    dataKey: "_evening_peak_mw",
    displayName: "Evening Peak",
    type: "demand",
    blockKey: "evening_peak_slot",
    blockLabel: "Evening Peak Block",
  },
  // Forecast D+1 Load metrics
  {
    dataKey: "_forecasted_peak_mw",
    displayName: "Forecast D+1 Day Peak",
    type: "demand_forecast_d1",
    blockKey: "forecasted_peak_slot",
    blockLabel: "Forecast D+1 Day Peak Block",
  },
  {
    dataKey: "_forecasted_morning_peak_mw",
    displayName: "Forecast D+1 Morning Peak",
    type: "demand_forecast_d1",
    blockKey: "forecasted_morning_peak_slot",
    blockLabel: "Forecast D+1 Morning Peak Block",
  },
  {
    dataKey: "_forecasted_evening_peak_mw",
    displayName: "Forecast D+1 Evening Peak",
    type: "demand_forecast_d1",
    blockKey: "forecasted_evening_peak_slot",
    blockLabel: "Forecast D+1 Evening Peak Block",
  },
  // Forecast D+2 Load metrics
  {
    dataKey: "_forecasted_d2_peak_mw",
    displayName: "Forecast D+2 Day Peak",
    type: "demand_forecast_d2",
    blockKey: "forecasted_d2_peak_slot",
    blockLabel: "Forecast D+2 Day Peak Block",
  },
  {
    dataKey: "_forecasted_d2_morning_peak_mw",
    displayName: "Forecast D+2 Morning Peak",
    type: "demand_forecast_d2",
    blockKey: "forecasted_d2_morning_peak_slot",
    blockLabel: "Forecast D+2 Morning Peak Block",
  },
  {
    dataKey: "_forecasted_d2_evening_peak_mw",
    displayName: "Forecast D+2 Evening Peak",
    type: "demand_forecast_d2",
    blockKey: "forecasted_d2_evening_peak_slot",
    blockLabel: "Forecast D+2 Evening Peak Block",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Convert month number + year to "Mon-YY"
function getMonthWithYear(month: number, year: number): string {
  if (month === undefined || year === undefined) return "";
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (month < 1 || month > 12) return "";
  return `${monthNames[month - 1]}-${year.toString().slice(-2)}`;
}

// Parse "Mon-YY" back to { month, year }
function parseMonthYear(monthYearStr: string | undefined): {
  month: number;
  year: number;
} {
  if (!monthYearStr) return { month: 0, year: 0 };
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const parts = monthYearStr.split("-");
  if (parts.length !== 2) return { month: 0, year: 0 };
  const [monthStr, yearStr] = parts;
  return {
    month: monthNames.indexOf(monthStr) + 1,
    year: 2000 + parseInt(yearStr),
  };
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ChhattisgarhPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  /** selectedSuffix is the array of active suffix objects from the parent */
  selectedSuffix: Array<{
    suffix: string;
    displayName: string;
    stackSuffix: string;
  }>;
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: Array<{
    suffix: string;
    displayName: string;
    stackSuffix: string;
  }>;
  metrics: TrendData | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ChhattisgarhPeakAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
  metrics,
}: ChhattisgarhPeakAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);

  // Single raw data state — fetched from the two Chhattisgarh peak APIs
  const [peakData, setPeakData] = useState<any[]>([]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const _handleFetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (!DefaultDates[activeTab]?.defaultRangeDate) {
        setLoading(false);
        return;
      }

      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDate = formatDateForAPI(dates.startDate);
      const endDate = formatDateForAPI(dates.endDate);

      if (selectedXaxis.key === "month") {
        // ── Monthly ────────────────────────────────────────────────────────
        const [actualRaw, forecastRaw] = await Promise.all([
          getChhattisgarhMonthlyPeakDiscom(startDate, endDate),
          getChhattisgarhMonthlyPeakForecast(startDate, endDate),
        ]);

        const actualByMonthYear = new Map<string, any>(
          (Array.isArray(actualRaw) ? actualRaw : []).map((item: any) => [
            `${item.month}-${item.year}`,
            item,
          ]),
        );
        const forecastByMonthYear = new Map<string, any>(
          (Array.isArray(forecastRaw) ? forecastRaw : []).map((f: any) => [
            `${f.month}-${f.year}`,
            f,
          ]),
        );

        // Union — actual and forecast are independent
        const allMonthYearKeys = new Set<string>([
          ...actualByMonthYear.keys(),
          ...forecastByMonthYear.keys(),
        ]);

        const toBlock = (v: any) =>
          v != null && v !== "" ? Math.round(Number(v)).toString() : "";

        const processed = Array.from(allMonthYearKeys).map((key) => {
          const item = actualByMonthYear.get(key) ?? {};
          const forecast = forecastByMonthYear.get(key) ?? {};
          const month = item.month ?? forecast.month;
          const year = item.year ?? forecast.year;
          return {
            month: getMonthWithYear(month, year),
            year,
            peak_mw: parseFloat(item.peak_mw?.toString() ?? "0"),
            morning_peak_mw: parseFloat(
              item.morning_peak_mw?.toString() ?? "0",
            ),
            evening_peak_mw: parseFloat(
              item.evening_peak_mw?.toString() ?? "0",
            ),
            average_mw: parseFloat(item.average_mw?.toString() ?? "0"),
            peak_slot: toBlock(item.timeblock_of_peak),
            morning_peak_slot: toBlock(item.timeblock_of_morning_peak),
            evening_peak_slot: toBlock(item.timeblock_of_evening_peak),
            forecasted_peak_mw: parseFloat(forecast.peak_mw?.toString() ?? "0"),
            forecasted_morning_peak_mw: parseFloat(
              forecast.morning_peak_mw?.toString() ?? "0",
            ),
            forecasted_evening_peak_mw: parseFloat(
              forecast.evening_peak_mw?.toString() ?? "0",
            ),
            forecasted_peak_slot: toBlock(forecast.timeblock_of_peak),
            forecasted_morning_peak_slot: toBlock(
              forecast.timeblock_of_morning_peak,
            ),
            forecasted_evening_peak_slot: toBlock(
              forecast.timeblock_of_evening_peak,
            ),
            forecasted_d2_peak_mw: parseFloat(
              forecast.d2_peak_mw?.toString() ?? "0",
            ),
            forecasted_d2_morning_peak_mw: parseFloat(
              forecast.d2_morning_peak_mw?.toString() ?? "0",
            ),
            forecasted_d2_evening_peak_mw: parseFloat(
              forecast.d2_evening_peak_mw?.toString() ?? "0",
            ),
            forecasted_d2_peak_slot: toBlock(forecast.d2_timeblock_of_peak),
            forecasted_d2_morning_peak_slot: toBlock(
              forecast.d2_timeblock_of_morning_peak,
            ),
            forecasted_d2_evening_peak_slot: toBlock(
              forecast.d2_timeblock_of_evening_peak,
            ),
          };
        });

        setPeakData(processed);
      } else {
        // ── Daily ──────────────────────────────────────────────────────────
        const [actualRaw, forecastRaw] = await Promise.all([
          getChhattisgarhDailyPeak(startDate, endDate),
          getChhattisgarhDailyForecastedPeak(startDate, endDate),
        ]);

        const actualByDate = new Map<string, any>(
          (Array.isArray(actualRaw) ? actualRaw : []).map((item: any) => [
            item.date,
            item,
          ]),
        );
        const forecastByDate = new Map<string, any>(
          (Array.isArray(forecastRaw) ? forecastRaw : []).map((f: any) => [
            f.date,
            f,
          ]),
        );

        // Union — actual and forecast are independent
        const allDatesUnion = new Set<string>([
          ...actualByDate.keys(),
          ...forecastByDate.keys(),
        ]);

        const toBlock = (v: any) =>
          v != null && v !== "" ? Math.round(Number(v)).toString() : "";

        const processed = Array.from(allDatesUnion).map((date) => {
          const item = actualByDate.get(date) ?? {};
          const forecast = forecastByDate.get(date) ?? {};
          return {
            date,
            peak_mw: parseFloat(item.peak_mw?.toString() ?? "0"),
            morning_peak_mw: parseFloat(
              item.morning_peak_mw?.toString() ?? "0",
            ),
            evening_peak_mw: parseFloat(
              item.evening_peak_mw?.toString() ?? "0",
            ),
            average_mw: parseFloat(item.average_mw?.toString() ?? "0"),
            peak_slot: toBlock(item.peak_slot),
            morning_peak_slot: toBlock(item.morning_peak_slot),
            evening_peak_slot: toBlock(item.evening_peak_slot),
            forecasted_peak_mw: parseFloat(forecast.peak_mw?.toString() ?? "0"),
            forecasted_morning_peak_mw: parseFloat(
              forecast.morning_peak_mw?.toString() ?? "0",
            ),
            forecasted_evening_peak_mw: parseFloat(
              forecast.evening_peak_mw?.toString() ?? "0",
            ),
            forecasted_peak_slot: toBlock(forecast.peak_slot),
            forecasted_morning_peak_slot: toBlock(forecast.morning_peak_slot),
            forecasted_evening_peak_slot: toBlock(forecast.evening_peak_slot),
            forecasted_d2_peak_mw: parseFloat(
              forecast.d2_peak_mw?.toString() ?? "0",
            ),
            forecasted_d2_morning_peak_mw: parseFloat(
              forecast.d2_morning_peak_mw?.toString() ?? "0",
            ),
            forecasted_d2_evening_peak_mw: parseFloat(
              forecast.d2_evening_peak_mw?.toString() ?? "0",
            ),
            forecasted_d2_peak_slot: toBlock(forecast.d2_peak_slot),
            forecasted_d2_morning_peak_slot: toBlock(
              forecast.d2_morning_peak_slot,
            ),
            forecasted_d2_evening_peak_slot: toBlock(
              forecast.d2_evening_peak_slot,
            ),
          };
        });

        setPeakData(processed);
      }
    } catch (error) {
      console.error("Error fetching Chhattisgarh peak data:", error);
      setPeakData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedXaxis.key]);

  useEffect(() => {
    _handleFetchData();
  }, [_handleFetchData]);

  // ── Chart data derivation ─────────────────────────────────────────────────
  useEffect(() => {
    if (loading) return;

    if (peakData.length === 0) {
      setChartData([]);
      setSummaryData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    // Sort chronologically
    const sorted = [...peakData].sort((a, b) => {
      if (selectedXaxis.key === "month") {
        const am = parseMonthYear(a.month);
        const bm = parseMonthYear(b.month);
        return am.year !== bm.year ? am.year - bm.year : am.month - bm.month;
      }
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    // Unit conversion
    const factor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    const converted = sorted.map((item) => ({
      ...item,
      peak_mw: (item.peak_mw ?? 0) * factor,
      morning_peak_mw: (item.morning_peak_mw ?? 0) * factor,
      evening_peak_mw: (item.evening_peak_mw ?? 0) * factor,
      average_mw: (item.average_mw ?? 0) * factor,
      forecasted_peak_mw: (item.forecasted_peak_mw ?? 0) * factor,
      forecasted_morning_peak_mw:
        (item.forecasted_morning_peak_mw ?? 0) * factor,
      forecasted_evening_peak_mw:
        (item.forecasted_evening_peak_mw ?? 0) * factor,
      forecasted_d2_peak_mw: (item.forecasted_d2_peak_mw ?? 0) * factor,
      forecasted_d2_morning_peak_mw:
        (item.forecasted_d2_morning_peak_mw ?? 0) * factor,
      forecasted_d2_evening_peak_mw:
        (item.forecasted_d2_evening_peak_mw ?? 0) * factor,
    }));

    const slotTableKeys = [
      "peak_slot",
      "morning_peak_slot",
      "evening_peak_slot",
      "forecasted_peak_slot",
      "forecasted_morning_peak_slot",
      "forecasted_evening_peak_slot",
      "forecasted_d2_peak_slot",
      "forecasted_d2_morning_peak_slot",
      "forecasted_d2_evening_peak_slot",
    ];

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys:
        selectedXaxis.key === "date"
          ? ["date", ...slotTableKeys]
          : ["month", ...slotTableKeys],
      groupBy: [],
      volumeKey: "",
      aggregationKeys: [
        "peak_mw",
        "morning_peak_mw",
        "evening_peak_mw",
        "forecasted_peak_mw",
        "forecasted_morning_peak_mw",
        "forecasted_evening_peak_mw",
        "forecasted_d2_peak_mw",
        "forecasted_d2_morning_peak_mw",
        "forecasted_d2_evening_peak_mw",
      ],
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: converted,
    };

    const cData = FormatChartData(par);

    // Merge slot data back into chart data for summary view
    const xKey = selectedXaxis.key === "month" ? "month" : "date";
    const slotByKey = Object.fromEntries(
      converted.map((item) => [
        item[xKey],
        {
          peak_slot: item.peak_slot,
          morning_peak_slot: item.morning_peak_slot,
          evening_peak_slot: item.evening_peak_slot,
          forecasted_peak_slot: item.forecasted_peak_slot,
          forecasted_morning_peak_slot: item.forecasted_morning_peak_slot,
          forecasted_evening_peak_slot: item.forecasted_evening_peak_slot,
          forecasted_d2_peak_slot: item.forecasted_d2_peak_slot,
          forecasted_d2_morning_peak_slot: item.forecasted_d2_morning_peak_slot,
          forecasted_d2_evening_peak_slot: item.forecasted_d2_evening_peak_slot,
        },
      ]),
    );
    const mergedData = cData.data.map((item) => ({
      ...item,
      ...(slotByKey[item[xKey]] ?? {}),
      block: slotByKey[item[xKey]]?.peak_slot ?? "-",
    }));

    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setSummaryData(mergedData);
    setChartKeys(cData.keys);
  }, [peakData, selectedXaxis, selectedUnit, loading]);

  // ── Active suffix keys derived from the Type filter (selectedSuffix) ──────
  // selectedSuffix contains objects like { suffix: "demand", ... } or
  // { suffix: "demand_forecast", ... }. We use these to decide which peak
  // metric groups are visible.
  const activeSuffixKeys = useMemo(() => {
    return new Set(selectedSuffix.map((s) => s.suffix));
  }, [selectedSuffix]);

  // ── Legends — filtered by the active Type selection ───────────────────────
  const legends = useMemo(() => {
    return ALL_PEAK_METRICS.filter((metric) =>
      activeSuffixKeys.has(metric.type),
    ).map((metric) => ({
      dataKey: metric.dataKey,
      name: metric.displayName,
      blockKey: metric.blockKey,
      legendColor: undefined,
      stroke: undefined,
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? metric.dataKey : undefined,
    }));
  }, [selectedChartType, activeSuffixKeys]);

  // Table legends: interleave block columns before each peak value
  const tableLegends = useMemo(() => {
    const filtered = ALL_PEAK_METRICS.filter((metric) =>
      activeSuffixKeys.has(metric.type),
    );
    const result: any[] = [];
    filtered.forEach((metric) => {
      // Block column first
      result.push({
        dataKey: metric.blockKey,
        name: metric.blockLabel,
        unit: "",
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
      });
      // Then the MW value column
      result.push({
        dataKey: metric.dataKey,
        name: metric.displayName,
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
      });
    });
    return result;
  }, [selectedChartType, activeSuffixKeys]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  // ── Summary helpers ───────────────────────────────────────────────────────
  const dateKey = selectedXaxis.key === "date" ? "date" : "month";
  const dateLabel = selectedXaxis.key === "date" ? "Date" : "Month";
  const xDataKey = selectedXaxis.values[0];

  // getSelectedTypes — keeps the Type FilterMenu in sync with the parent
  function getSelectedTypes() {
    return selectedSuffix.map((s) => s.displayName);
  }

  const baseProps = {
    legends: visibleLegends as any,
    data: summaryData,
    tableData,
    unit: selectedUnit,
    filename: AddDatesWithFileName({
      fileName: "Forecast Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  // Summary: one Block column resolved per-legend via blockKey
  const summaryProps = {
    ...baseProps,
    extraKeys: [dateKey, "block"],
    extraLabels: [dateLabel, "Block"],
  };

  // Table: interleaved Block | MW pairs per peak type
  const tableProps = {
    ...baseProps,
    legends: tableLegends as any,
    extraKeys: [dateKey],
    extraLabels: [dateLabel],
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(_, index) => setSelectedXaxis(X_AXIS[index])}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />

          {/* <FilterMenu
            title="Discom"
            items={DISCOM_OPTIONS}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={onDiscomChange}
          /> */}

          {/* Type filter — reflects and updates the parent's selection */}
          <FilterMenu
            title="Type"
            items={chartSuffixes
              .filter((s) => s.suffix !== "deviation")
              .map((s) => s.displayName)}
            selectedItems={getSelectedTypes()}
            setSelectedItems={onTypeChange}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, index) => setSelectedChartType(H_CHART_TYPES[index])}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Forecast Peak Analysis"
              subtitle="Fetching Chhattisgarh forecast peak analysis data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-100">
                  Forecast Peak Analysis ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart") {
                        downloadChartTableCSV(tableProps);
                      } else if (dataTab.label === "Summary") {
                        downloadSummaryCSV(summaryProps);
                      } else if (dataTab.label === "Table") {
                        downloadChartTableCSV(tableProps);
                      }
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedGroupKeys}-${selectedUnit}-${selectedXaxis.key}-${selectedChartType.value}`}
                  data={chartData}
                  showBrush={true}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends as any}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  secondXDataKey={undefined}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...summaryProps} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable {...tableProps} defaultSortDirection="asc" />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>CSPDCL</p>}
              text="
            1. Actual Load data is sourced from CSPDCL's official records.
            2. Forecast Load data is provided by GNA Energy."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
